<script setup lang="ts">
// This component doesn't fire any mutations. Instead it emits events to the parent component.

import StatusDisplay from '@/components/rc/miner-repairs/repairs-sidebar/diagnostics-elements/StatusDisplay.vue'
import { useFailureTypes } from '@/composables/rc/failure-types'
import { usePartDetails } from '@/composables/rc/part-details'
import { DiagnosticStatusIcons, PartDiagnosticStatus } from '@/types/rc/miners/PartStatus'
import { partIcons, type IndividualPart } from '@/types/rc/miners/PartType'
import { Icon } from '@iconify/vue'
import { computed, ref, toRef } from 'vue'
import EditableNotesField from '../../../common/EditableNotesField.vue'
import FailureTypeSelect from './FailureTypeSelect.vue'

const emit = defineEmits(['flagPartGood', 'flagPartFail', 'flaggingFail'])
const props = defineProps<{ minerId: number; partName: IndividualPart; disabled: boolean }>()

const { isFailureTypeOther, isFailureTypeASICN } = useFailureTypes()

// Buffers to select the failure type
const flaggingFailed = ref(false)
const selectedFailureType = ref<number | undefined>()
const flaggingFailTypeOther = computed(() => selectedFailureType.value && isFailureTypeOther(selectedFailureType.value))
const flaggingFailTypeASICN = computed(() => selectedFailureType.value && isFailureTypeASICN(selectedFailureType.value))
const asicN = ref(1)

// Server data:
const {
  partId,
  partType,
  initialDiagnostic,
  isFailing,
  hasFailureTypeOther,
  failureDescription,
  failureReason,
  repairNotes,
} = usePartDetails(toRef(props, 'minerId'), toRef(props, 'partName'))

const flagPartGood = () => {
  emit('flagPartGood', partId.value, props.partName)
}

const flagPartFail = () => {
  flaggingFailed.value = !flaggingFailed.value
  emit('flaggingFail', flaggingFailed.value, props.partName)
}

const updateFailureType = (failType: number | undefined) => {
  if (!failType) return
  if (!isFailureTypeOther(failType) && !isFailureTypeASICN(failType)) {
    emit('flagPartFail', partId.value, props.partName, failType)
    emit('flaggingFail', false, props.partName)
    flaggingFailed.value = false
  }
}

const saveFailureReason = (text: string, discardNotes?: () => void) => {
  emit('flagPartFail', partId.value, props.partName, selectedFailureType.value, text, discardNotes)
  emit('flaggingFail', false, props.partName)
  flaggingFailed.value = false
}

const iconGood = DiagnosticStatusIcons[PartDiagnosticStatus.Good]
const iconFail = DiagnosticStatusIcons[PartDiagnosticStatus.Fail]
</script>

<template>
  <!-- These are the grid "colums". Span is determined in styles. -->
  <!-- They all get icon and title -->
  <Icon class="row-icon" :icon="partIcons[partType]" />
  <p>{{ partName }}</p>
  <!-- Buttons: If the part is null, just show a message -->
  <p v-if="!partId" class="no-part">Unavailable</p>
  <!-- Buttons: If the part exists and has no status, show Good/Fail buttons to update -->
  <el-button
    v-if="partId && !initialDiagnostic"
    class="btn-good"
    :icon="iconGood"
    :disabled="flaggingFailed || disabled"
    @click="flagPartGood"
  >
    Good
  </el-button>
  <el-button
    v-if="partId && !initialDiagnostic"
    class="btn-fail"
    :icon="iconFail"
    :disabled="disabled"
    @click="flagPartFail"
  >
    Fail
  </el-button>
  <!-- Buttons: If the part has a status other than Fail, just display that "status button" -->
  <StatusDisplay v-if="initialDiagnostic && !isFailing" :status="initialDiagnostic!" />
  <!-- Buttons: If it's failing with failure type different than 'Other', put its description on the tooltip -->
  <StatusDisplay v-if="isFailing && !hasFailureTypeOther" :tooltip="failureDescription" :status="initialDiagnostic!" />
  <!-- Buttons: If it's failing and has failure type 'Other', put the reason on tooltip -->
  <StatusDisplay v-if="isFailing && hasFailureTypeOther" :status="PartDiagnosticStatus.Fail" :tooltip="failureReason" />
  <!-- Failure select: If the part is being set as failed, show failure select -->
  <FailureTypeSelect
    v-if="flaggingFailed"
    v-model="selectedFailureType"
    :part-type="partType"
    @update:model-value="updateFailureType"
  />
  <!-- Failure reason: If it's failing and selected failure is type 'Other', display a text input for the reason -->
  <EditableNotesField
    v-if="flaggingFailed && flaggingFailTypeOther"
    class="failure-reason"
    :cache-value="repairNotes ?? ''"
    placeholder="Describe failure"
    express
    @save="saveFailureReason"
  />
  <!-- # ASIC: If it's failing and selected failure is type '# ASIC', display a number input for the quantity -->
  <div v-if="flaggingFailed && flaggingFailTypeASICN" class="asicn flex justify-between gap-2">
    <el-input-number v-model="asicN" size="small" placeholder="# ASIC" />
    <el-button type="primary" @click="saveFailureReason(asicN.toString())">Save</el-button>
  </div>
</template>

<style lang="scss">
.row-icon path {
  fill: var(--text-tertiary);
}

.btn-good path {
  fill: var(--component-stroke-emerald);
}

.btn-fail path {
  fill: var(--component-stroke-error);
}

.btn-good.selected,
.btn-fail.selected {
  background-color: var(--fill-tertiary);
  border: none;
}

.is-disabled {
  path {
    fill: var(--component-fill-gray);
  }
}

.no-part {
  grid-column: 3/5;
  text-align: center;
  color: var(--text-tertiary);
}

.diagnostics-table .failure-type-select {
  grid-column: 2/5;
  color: var(--text-tertiary);
  display: flex;
  align-items: center;
  justify-content: space-between;

  .el-select {
    width: 180px;
    display: inline-block;
    background: none;
    border: none;

    &__wrapper {
      border: 1px solid var(--fill-brand);
      background: none;
    }
  }
}

.failure-reason {
  grid-column: 2/5;
  color: var(--text-tertiary);
}

.asicn {
  grid-column: 3/5;
}

.btn-status {
  grid-column: 3/5;
  cursor: default;
  color: var(--component-stroke-error);

  .el-icon path {
    fill: var(--component-stroke-error);
  }

  &.ok {
    color: var(--component-stroke-emerald);

    .el-icon path {
      fill: var(--component-stroke-emerald);
    }
  }
}
</style>
